import React, { useEffect, useRef, useState } from 'react';
import './MainContent.css';
import cityImage from '../Night-Cityscape-With-Skyscrapers-1300x650.png';
import startBgImage from '../start-bg.png';

interface MediaItem {
  id: string;
  type: 'image' | 'video';
  url: string;
  title: string;
  description: string;
  category: string;
  thumbnail?: string;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  order: number;
}

interface SocialLink {
  id: string;
  name: string;
  url: string;
  icon: string;
}

interface ContentSection {
  id: string;
  title: string;
  description: string;
}

const MainContent: React.FC = () => {
  const titleRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState('home');
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [contentSections, setContentSections] = useState<ContentSection[]>([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('');
  const [currentImage, setCurrentImage] = useState({ url: '', title: '' });

  // LocalStorage'dan medya verilerini yükle
  useEffect(() => {
    const savedMedia = localStorage.getItem('vise-media');
    const savedFaqs = localStorage.getItem('vise-faqs');
    const savedSocialLinks = localStorage.getItem('vise-social-links');
    const savedContentSections = localStorage.getItem('vise-content-sections');
    
    if (savedMedia) {
      setMediaItems(JSON.parse(savedMedia));
    }

    if (savedFaqs) {
      setFaqs(JSON.parse(savedFaqs));
    } else {
      // Eğer localStorage'da FAQ yoksa, varsayılan FAQ'ları kullan
      setFaqs([
        {
          id: '1',
          question: 'GTA V\'de Role Play nedir?',
          answer: 'Role Play, seçilen rolleri oynayarak, kuralları takip ederek ve gerçek hayattaki gibi etkileşim kurarak oynanan popüler bir oyun türüdür.',
          order: 1
        },
        {
          id: '2',
          question: 'VISE RP\'de nasıl başlarım?',
          answer: 'Başlamak için önce GTA V lisansını satın almanız ve ardından VISE Launcher\'ı indirmeniz gerekiyor.',
          order: 2
        },
        {
          id: '3',
          question: 'Sunucuda yeni oyuncu ne yapmalı?',
          answer: 'Yeni oyuncular için temel mekanikleri ve oyun kurallarını öğrenmeye yardımcı olacak eğitim sistemimizi tamamlamanızı öneriyoruz.',
          order: 3
        },
        {
          id: '4',
          question: 'VISE RP rehberleri ve Wiki\'yi nerede bulabilirim?',
          answer: 'Detaylı rehberler, mekanik açıklamaları ve meslek bilgileri resmi Wiki\'mizde - oyuncular için kullanışlı bilgi tabanında toplanmıştır.',
          order: 4
        },
        {
          id: '5',
          question: 'Sunucu kurallarını nerede bulabilirim?',
          answer: 'Sunucu kuralları, oyun içi kurallar ve etkinlik bilgileri forum sayfamızda bulunabilir.',
          order: 5
        },
        {
          id: '6',
          question: 'VISE RP\'ye giriş yapamıyorum?',
          answer: 'Giriş sorunları yaşıyorsanız resmi Discord kanalımızdaki teknik destek ekibimizle iletişime geçmenizi öneriyoruz.',
          order: 6
        }
      ]);
    }

    if (savedSocialLinks) {
      setSocialLinks(JSON.parse(savedSocialLinks));
    } else {
      // Eğer localStorage'da sosyal medya linkleri yoksa, varsayılan linkleri kullan
      setSocialLinks([
        {
          id: 'discord',
          name: 'Discord',
          url: 'https://discord.gg/ZRPhecHk',
          icon: '💬'
        },
        {
          id: 'youtube',
          name: 'YouTube',
          url: 'https://youtube.com/@viserp',
          icon: '📺'
        },
        {
          id: 'instagram',
          name: 'Instagram',
          url: 'https://instagram.com/viserp',
          icon: '📷'
        },
        {
          id: 'tiktok',
          name: 'TikTok',
          url: 'https://tiktok.com/@viserp',
          icon: '🎵'
        }
      ]);
    }

    if (savedContentSections) {
      setContentSections(JSON.parse(savedContentSections));
    } else {
      // Varsayılan içerik bölümleri
      setContentSections([
        {
          id: 'about',
          title: 'KENDİ YOLUNU ÇİZ',
          description: 'Bu dinamik VISE RP\'de seçim senin; kötü şöhretli bir çete lider ol ya da senaryoyu değiştir ve adaletin savunucusu olmak için güçlere katıl. Suç ve kanun arasındaki ince çizgide gezin, her karar seni yeraltı dünyasının derinliklerine ya da şehrin en cesurlarının saflarına itebilir. Sokakları mı yöneteceksin yoksa onları mı koruyacaksın?'
        }
      ]);
    }
  }, []);

  // Medya öğesini ID ile bul
  const getMediaItem = (id: string) => {
    return mediaItems.find(item => item.id === id);
  };

  // Video modal açma
  const openVideoModal = (videoUrl: string) => {
    // YouTube URL'sini embed formatına çevir
    let embedUrl = videoUrl;
    if (videoUrl.includes('youtube.com/watch?v=')) {
      const videoId = videoUrl.split('v=')[1]?.split('&')[0];
      embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } else if (videoUrl.includes('youtu.be/')) {
      const videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
      embedUrl = `https://www.youtube.com/embed/${videoId}`;
    }
    setCurrentVideo(embedUrl);
    setShowVideoModal(true);
  };

  // Resim modal açma
  const openImageModal = (imageUrl: string, title: string) => {
    setCurrentImage({ url: imageUrl, title });
    setShowImageModal(true);
  };

  // Modal kapatma
  const closeModals = () => {
    setShowVideoModal(false);
    setShowImageModal(false);
    setCurrentVideo('');
    setCurrentImage({ url: '', title: '' });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!titleRef.current) return;
      
      const rect = titleRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const mouseX = e.clientX - centerX;
      const mouseY = e.clientY - centerY;
      
      const rotateX = (mouseY / window.innerHeight) * -20;
      const rotateY = (mouseX / window.innerWidth) * 20;
      
      titleRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
    };

    const handleScroll = () => {
      const sections = ['home', 'about', 'start', 'faq'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
    <main className="main-content">
      {/* Floating Navigation */}
      <div className="floating-nav">
        <button 
          className={`float-nav-btn ${activeSection === 'home' ? 'active' : ''}`}
          onClick={() => scrollToSection('home')}
          title="Ana Sayfa"
        >
          🏠
        </button>
        <button 
          className={`float-nav-btn ${activeSection === 'about' ? 'active' : ''}`}
          onClick={() => scrollToSection('about')}
          title="Hakkımızda"
        >
          ℹ️
        </button>
        <button 
          className={`float-nav-btn ${activeSection === 'start' ? 'active' : ''}`}
          onClick={() => scrollToSection('start')}
          title="Nasıl Başlarım"
        >
          🚀
        </button>
        <button 
          className={`float-nav-btn ${activeSection === 'faq' ? 'active' : ''}`}
          onClick={() => scrollToSection('faq')}
          title="Sık Sorulan Sorular"
        >
          ❓
        </button>
      </div>

      {/* Hero Section */}
      <section 
        id="home"
        className="hero-section"
        style={{
          backgroundImage: `url(${cityImage})`
        }}
      >
        <div className="vise-title" ref={titleRef}>
          <h1>
            <span className="letter-v">V</span>
            <span className="letter-i">I</span>
            <span className="letter-s">S</span>
            <span className="letter-e">E</span>
          </h1>
          <button 
            className="join-button"
            onClick={() => {
              const discordLink = socialLinks.find(link => link.id === 'discord');
              if (discordLink) {
                window.open(discordLink.url, '_blank');
              } else {
                window.open('https://discord.gg/ZRPhecHk', '_blank');
              }
            }}
          >
            ARAMIZA KATIL
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="about-content">
            <div className="about-main">
              <div className="about-left">
                <h2>{contentSections.find(section => section.id === 'about')?.title || 'KENDİ YOLUNU ÇİZ'}</h2>
                <p>
                  {contentSections.find(section => section.id === 'about')?.description || 
                   'Bu dinamik VISE RP\'de seçim senin: kötü şöhretli bir çete lider ol ya da senaryoyu değiştir ve adaletin savunucusu olmak için güçlere katıl. Suç ve kanun arasındaki ince çizgide gezin, her karar seni yeraltı dünyasının derinliklerine ya da şehrin en cesurlarının saflarına itebilir. Sokakları mı yöneteceksin yoksa onları mı koruyacaksın?'}
                </p>
              </div>
              <div className="about-right">
                <div className="video-showcase">
                  <div className="main-video" 
                       style={{ 
                         backgroundImage: `url(${getMediaItem('main-video')?.thumbnail || '/gta.png'})`,
                         backgroundSize: 'cover',
                         backgroundPosition: 'center'
                       }}
                       onClick={() => {
                    const mainVideo = getMediaItem('main-video');
                    if (mainVideo && mainVideo.url) {
                      openVideoModal(mainVideo.url);
                    }
                  }}>
                    <div className="video-overlay">
                      <div className="play-button">▶</div>
                      <div className="video-text">
                        <h3>{getMediaItem('main-video')?.title || 'VISE RP TANITIM VİDEOSU'}</h3>
                        <p>{getMediaItem('main-video')?.description || 'GTA 5 ROLEPLAY DENEYİMİ'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="video-grid">
                    <div className="video-thumb gta-thumb" 
                         style={{ backgroundImage: `url(${getMediaItem('gta-thumb')?.url || '/gta.png'})` }}
                         onClick={() => {
                           const item = getMediaItem('gta-thumb');
                           if (item) openImageModal(item.url, item.title);
                         }}>
                      <div className="thumb-overlay">
                        <span>{getMediaItem('gta-thumb')?.title || 'GTA 5'}</span>
                      </div>
                    </div>
                    <div className="video-thumb fivem-thumb" 
                         style={{ backgroundImage: `url(${getMediaItem('fivem-thumb')?.url || '/fivem.png'})` }}
                         onClick={() => {
                           const item = getMediaItem('fivem-thumb');
                           if (item) openImageModal(item.url, item.title);
                         }}>
                      <div className="thumb-overlay">
                        <span>{getMediaItem('fivem-thumb')?.title || 'FiveM'}</span>
                      </div>
                    </div>
                    <div className="video-thumb gameplay-thumb" 
                         style={{ backgroundImage: `url(${getMediaItem('gameplay-thumb')?.url || '/gta.png'})` }}
                         onClick={() => {
                           const item = getMediaItem('gameplay-thumb');
                           if (item) openImageModal(item.url, item.title);
                         }}>
                      <div className="thumb-overlay">
                        <span>{getMediaItem('gameplay-thumb')?.title || 'Gameplay'}</span>
                      </div>
                    </div>
                    <div className="video-thumb city-thumb" 
                         style={{ backgroundImage: `url(${getMediaItem('city-thumb')?.url || '/gta.png'})` }}
                         onClick={() => {
                           const item = getMediaItem('city-thumb');
                           if (item) openImageModal(item.url, item.title);
                         }}>
                      <div className="thumb-overlay">
                        <span>{getMediaItem('city-thumb')?.title || 'Los Santos'}</span>
                      </div>
                    </div>
                    <div className="video-thumb cars-thumb" 
                         style={{ backgroundImage: `url(${getMediaItem('cars-thumb')?.url || '/gta.png'})` }}
                         onClick={() => {
                           const item = getMediaItem('cars-thumb');
                           if (item) openImageModal(item.url, item.title);
                         }}>
                      <div className="thumb-overlay">
                        <span>{getMediaItem('cars-thumb')?.title || 'Araçlar'}</span>
                      </div>
                    </div>
                    <div className="video-thumb jobs-thumb" 
                         style={{ backgroundImage: `url(${getMediaItem('jobs-thumb')?.url || '/gta.png'})` }}
                         onClick={() => {
                           const item = getMediaItem('jobs-thumb');
                           if (item) openImageModal(item.url, item.title);
                         }}>
                      <div className="thumb-overlay">
                        <span>{getMediaItem('jobs-thumb')?.title || 'Meslekler'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="more-photos">
                    <span>DAHA</span>
                    <span>FAZLA</span>
                    <div className="arrow">→</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Start Section */}
      <section 
        id="start" 
        className="start-section"
        style={{
          backgroundImage: `url(${startBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="section-container">
          <div className="start-content">
            <div className="start-left">
              <h2>NASIL OYNAMAYA BAŞLARIM?</h2>
              <p>Sadece iki basit adım ve oynamaya hazırsın!</p>
              
              <div className="start-steps">
                <div className="step-item">
                  <div className="step-icon">
                    <img src={getMediaItem('gta-step')?.url || '/gta.png'} alt="GTA V" />
                  </div>
                  <div className="step-content">
                    <h3>Grand Theft Auto V Satın Al</h3>
                    <p>Eğer lisanslı bir versiyonunuz yoksa</p>
                  </div>
                  <button 
                    className="step-button buy-button"
                    onClick={() => window.open('https://store.steampowered.com/app/3240220/Grand_Theft_Auto_V_Enhanced', '_blank')}
                  >
                    SATIN AL
                  </button>
                </div>
                
                <div className="step-item">
                  <div className="step-icon">
                    <img src={getMediaItem('fivem-step')?.url || '/fivem.png'} alt="FiveM" />
                  </div>
                  <div className="step-content">
                    <h3>FiveM İndir</h3>
                    <p>FiveM gerekli sunucu dosyalarını indirecek</p>
                  </div>
                  <button 
                    className="step-button download-button"
                    onClick={() => window.open('https://servers.fivem.net/', '_blank')}
                  >
                    İNDİR
                  </button>
                </div>
              </div>
              
              <div className="contact-info">
                <span>Sorularınız mı var? 💬 Bizimle iletişime geçin!</span>
              </div>
            </div>
            
            <div className="start-right">
              <h2>BİZİMLE BAĞLANTIDA KALIN</h2>
              <p>Promosyonlar ve güncellemeler hakkında ilk siz haberdar olun</p>
              
              <div className="social-grid">
                {socialLinks.map(link => (
                  <div 
                    key={link.id}
                    className={`social-item ${link.id}`}
                    onClick={() => window.open(link.url, '_blank')}
                  >
                    <div className="social-icon">
                      <img src={getMediaItem(`${link.id}-social`)?.url || `/${link.id}.png`} alt={link.name} />
                    </div>
                    <span>{link.name}</span>
                  </div>
                ))}
              </div>
              
              <div className="media-partner">
                <span>Medya ortağı olmak ister misiniz? 💬 Bizimle iletişime geçin!</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="section-container">
          <div className="faq-content">
            <h2>SIK SORULAN SORULAR</h2>
            <div className="faq-grid">
              {faqs.sort((a, b) => a.order - b.order).map(faq => (
                <div key={faq.id} className="faq-item">
                  <h3>{faq.question}</h3>
                  <p>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-section">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-main">
              <div className="footer-brand">
                <div className="footer-logo">
                  <span className="logo-v">V</span>
                  <span className="logo-i">I</span>
                  <span className="logo-s">S</span>
                  <span className="logo-e">E</span>
                  <span className="brand-text">ROLE<br />PLAY</span>
                </div>
                <p className="footer-description">
                  Türkiye'nin en kaliteli GTA 5 roleplay deneyimini sunan sunucusudur.
                </p>
              </div>
              
              <div className="footer-links">
                <div className="footer-column">
                  <h4>Hızlı Linkler</h4>
                  <ul>
                    <li><button onClick={() => scrollToSection('home')} className="footer-link"><i className="icon">🏠</i> Ana Sayfa</button></li>
                    <li><button onClick={() => scrollToSection('start')} className="footer-link"><i className="icon">🎮</i> Oynamaya Başla</button></li>
                  </ul>
                </div>
                
                <div className="footer-column">
                  <h4>Topluluk</h4>
                  <ul>
                    {socialLinks.map(link => (
                      <li key={link.id}>
                        <button 
                          className="footer-link"
                          onClick={() => window.open(link.url, '_blank')}
                        >
                          <i className="icon">{link.icon}</i> {link.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="footer-column">
                  <h4>Destek</h4>
                  <ul>
                    <li><button className="footer-link"><i className="icon">💝</i> Donate</button></li>
                  </ul>
                </div>
                
                <div className="footer-column">
                  <h4>Hakkımızda</h4>
                  <ul>
                    <li><button onClick={() => scrollToSection('about')} className="footer-link"><i className="icon">ℹ️</i> Hakkımızda</button></li>
                    <li><button className="footer-link"><i className="icon">📞</i> İletişim</button></li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="footer-bottom">
              <div className="footer-bottom-content">
                <p className="copyright">
                  2026 VISE Roleplay • <span className="heart">❤️</span> ile geliştirildi • Tüm hakları saklıdır
                </p>
                <div className="footer-badges">
                  <span className="badge">🔒 Kullanım Şartları</span>
                  <span className="badge">🛡️ Gizlilik Politikası</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </main>

    {/* Video Modal */}
    {showVideoModal && (
      <div className="modal-overlay" onClick={closeModals}>
        <div className="video-modal" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={closeModals}>✕</button>
          <iframe
            src={currentVideo}
            title="Video Player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </div>
    )}

    {/* Image Modal */}
    {showImageModal && (
      <div className="modal-overlay" onClick={closeModals}>
        <div className="image-modal" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={closeModals}>✕</button>
          <img src={currentImage.url} alt={currentImage.title} />
          <div className="image-title">{currentImage.title}</div>
        </div>
      </div>
    )}
  </>
  );
};

export default MainContent;